import { get } from 'svelte/store';
// Design System
import { SystemStyles } from '@Shelly/shelly-design-system/index';
// Helpers
import { getSystemStyle } from '../../ui/chart_colors_helpers';
import { convertToUnit, convertCost, convertToValueAndUnit } from 'src/helpers/units_helpers';
import { average, mathEpsilon, sum } from 'src/helpers/utilities';
import { isEM } from 'src/helpers/interface_helpers';
import { user_settings } from 'src/stores';
//Types
import type { ChartConfig, ChartStatisticInterface } from 'src/types/Chart';

export const getTotalConsumption = (
	totalConsumption: number,
	totalConsumptionCost: number,
): { icon: string; value: string }[] => {
	const inPowerTotal = convertToValueAndUnit(totalConsumption, 'Wh');
	let labelListConsum = [];
	if (inPowerTotal.value) {
		labelListConsum = [
			{
				icon: 'fas fa-bolt',
				value: `${inPowerTotal.value} ${inPowerTotal.unit}`,
				iconColor: getSystemStyle(SystemStyles.COLOR_PRIMARY),
			},
		];
		if (totalConsumptionCost) {
			const sumCostFixed = convertCost(totalConsumptionCost, get(user_settings).currency);
			if (sumCostFixed) {
				labelListConsum.push({
					icon: 'fas fa-coins',
					value: `${sumCostFixed}`,
					iconColor: getSystemStyle(SystemStyles.COLOR_CHART_MONEY_VALUE),
				});
			}
		}
	}
	return labelListConsum;
};

export const getTotalReturnedConsumption = (
	totalReturnedConsumption: number,
): { icon: string; value: string }[] => {
	const inPowerTotal = convertToValueAndUnit(Math.abs(totalReturnedConsumption), 'Wh');
	let labelListConsum = [];
	if (inPowerTotal.value) {
		labelListConsum = [
			{
				icon: 'fas fa-house-day',
				value: `${inPowerTotal.value} ${inPowerTotal.unit}`,
				iconColor: getSystemStyle(SystemStyles.COLOR_SUCCESS),
			},
		];
	}
	return labelListConsum;
};

export const estimateChartConsumption = (
	chartData: ChartConfig,
	deviceType: string,
	currency: string,
	threeEmData: { channel: string; prevChartData: ChartConfig } = null,
): ChartStatisticInterface => {
	const dataTotalIndex = threeEmData
			? chartData.series.findIndex((i) => i.name == threeEmData.channel)
			: 0,
		data = chartData.series[dataTotalIndex].data,
		valuesFilter = data.filter((v) => v.metaData !== null && !v?.missing),
		values = valuesFilter.map((v) => v.value),
		total = sum(values),
		costData = data.filter((v) => v.cost !== null && !v?.missing),
		historyIndex = isEM(deviceType) ? 2 : threeEmData ? dataTotalIndex : 1;

	const dataReturnedIndex =
			threeEmData && chartData.deviceMode !== 'monophase'
				? chartData.series.findIndex((i) => i.name == threeEmData.channel + '_returned')
				: chartData.series.findIndex((i) => i.name == 'reversed'),
		dataReturned = dataReturnedIndex >= 0 ? chartData.series[dataReturnedIndex].data : [],
		valuesFilterReturned = dataReturned.filter((v) => v.metaData !== null && !v?.missing),
		valuesReturned = valuesFilterReturned.map((v) => v.value),
		totalReturned = Math.abs(sum(valuesReturned));

	const result: ChartStatisticInterface = {
		name: chartData.name,
		available: total > 0 || totalReturned > 0,
		totalConsumed: convertToUnit(total, chartData.units),
		totalConsumedValue: total,
		totalGenerated: totalReturned > 0 ? convertToUnit(totalReturned, chartData.units) : undefined,
		totalGeneratedValue: totalReturned,
		average: convertToUnit(average(values), chartData.units),
		min: convertToUnit(Math.min(...values), chartData.units),
		max: convertToUnit(Math.max(...values), chartData.units),
		prevTotalConsumedValue: null,
		comparison: null,
		comparisonDiff: null,
		totalCost: null,
		averageCost: null,
		minCost: null,
		maxCost: null,
		threeEMStatistic: null,
		phaseName: null,
		tariffs: null,
		returnedAverage: null,
		returnedMin: null,
		returnedMax: null,
	};

	if (costData && costData.length) {
		const cost = costData.map((v) => Number(v.cost));
		result.totalCost = convertCost(sum(cost), currency);
		result.averageCost = convertCost(average(cost), currency);
		const min = valuesFilter.find((v) => v.value == Math.min(...values)),
			max = valuesFilter.find((v) => v.value == Math.max(...values));
		if (max && max.convertCost) {
			result.maxCost = max.convertCost;
			result.minCost = `0 ${currency || ''}`;
			if (min && min.convertCost) {
				result.minCost = min.convertCost;
			}
		}
		const metaData = costData.map((v) => v.metaData);
		if (metaData?.length) {
			const tariffs = {},
				tariffData = metaData.flat();
			for (let i = 0; i < tariffData.length; ++i) {
				const tariff = tariffData[i]?.tariff;
				if (!tariff) continue;
				if (!tariffs[tariff.id]) {
					tariffs[tariff.id] = tariff;
				} else {
					tariffs[tariff.id].value += tariff.value;
					tariffs[tariff.id].cost += tariff.cost;
				}
			}
			result.tariffs = [];
			for (const tariffID in tariffs) {
				const tariff = tariffs[tariffID];
				result.tariffs.push({
					name: tariff.name,
					consumed: convertToUnit(tariff.value, tariff.unit),
					cost: convertCost(tariff.cost, tariff.currency),
				});
			}
		}
	}

	const prevValues = ((threeEmData?.prevChartData || chartData)?.series?.[historyIndex]?.data || [])
			.slice(0, values.length)
			.map((v) => v.value),
		prevTotal = sum(prevValues);
	if (prevTotal > 0) {
		result.prevTotal = prevTotal;
		result.comparison = total <= prevTotal;
		result.comparisonDiff = mathEpsilon(Math.abs(100 - (total / prevTotal) * 100), 1);
	}

	if (threeEmData?.channel == 'total') {
		result.phaseName = 'total';
		result.threeEMStatistic = [];
		for (let i = 0; i < chartData.series.length; ++i) {
			const phaseData = chartData.series[i];
			if (!/total/.test(phaseData.name)) {
				const phase = estimateChartConsumption(chartData, deviceType, currency, {
					...threeEmData,
					channel: phaseData.name,
				});
				result.threeEMStatistic.push({ ...phase, phaseName: phaseData.name });
			}
		}
	}

	if (totalReturned > 0) {
		result.returnedAverage = convertToUnit(Math.abs(average(valuesReturned)), chartData.units);
		result.returnedMin = convertToUnit(Math.abs(Math.min(...valuesReturned)), chartData.units);
		result.returnedMax = convertToUnit(Math.abs(Math.max(...valuesReturned)), chartData.units);
	}

	return result;
};
